// English/Dutch for the portal (vue-i18n). One Dutch for the Netherlands and
// Flanders; every word is fixed in docs/reference/dutch_glossary.md, so a new
// Dutch string uses the glossary term or the glossary changes first.
//
// Which language: the backend resolves it (user's choice -> store default ->
// English) and returns it as `language` from /get_user_details; UserStore calls
// setLanguage() with it. Before login (and on public pages) the last language
// used on this browser applies.
//
// English is the fallback: a key missing in nl.json shows the English text, so
// screens can be translated one at a time.

import { createI18n } from 'vue-i18n'

// One file per AREA and language: locales/en/today.json holds the keys under
// `today.` (t('today.hero.title')). Several areas can be translated side by side
// without touching the same file. The file name is the top-level key.
function loadLocale(files) {
  const out = {}
  for (const [path, mod] of Object.entries(files)) {
    const area = path.split('/').pop().replace(/\.json$/, '')
    out[area] = mod.default || mod
  }
  return out
}
const en = loadLocale(import.meta.glob('./locales/en/*.json', { eager: true }))
const nl = loadLocale(import.meta.glob('./locales/nl/*.json', { eager: true }))

export const SUPPORTED = ['en', 'nl']
export const DEFAULT_LANGUAGE = 'en'
const STORAGE_KEY = 'storalytic.language'

// 'nl', 'NL', 'nl-BE' -> 'nl'; anything unsupported -> null. Mirrors applicaite/i18n.normalize.
export function normalizeLanguage(code) {
  if (!code || typeof code !== 'string') return null
  const base = code.trim().replace('_', '-').split('-')[0].toLowerCase()
  return SUPPORTED.includes(base) ? base : null
}

function storedLanguage() {
  try {
    return normalizeLanguage(localStorage.getItem(STORAGE_KEY))
  } catch {
    return null
  }
}

export const i18n = createI18n({
  legacy: false,
  locale: storedLanguage() || DEFAULT_LANGUAGE,
  fallbackLocale: DEFAULT_LANGUAGE,
  messages: { en, nl },
  // Untranslated keys fall back to English quietly; the console noise would
  // drown real errors while screens are translated one by one.
  missingWarn: false,
  fallbackWarn: false,
})

// <html lang> must match from the first paint, not only after login.
if (typeof document !== 'undefined') document.documentElement.lang = i18n.global.locale.value

// The language currently shown ('en' | 'nl').
export function currentLanguage() {
  return i18n.global.locale.value
}

// Switch the whole portal. Also sets <html lang> (screen readers, the browser's
// translate offer, hyphenation) and remembers it for the login page.
export function setLanguage(code) {
  const lang = normalizeLanguage(code) || DEFAULT_LANGUAGE
  i18n.global.locale.value = lang
  if (typeof document !== 'undefined') document.documentElement.lang = lang
  try {
    localStorage.setItem(STORAGE_KEY, lang)
  } catch {
    /* private mode: the choice just isn't remembered on this browser */
  }
  return lang
}

export default i18n

// The store language suggested for a new store's country (ISO code). Mirrors the
// backend's i18n.default_for_country, except that a country with more than one
// language returns '' so the registration form ASKS instead of guessing.
const COUNTRY_LANGUAGE = { NL: 'nl' }
const MULTILINGUAL = new Set(['BE', 'CH', 'LU'])
export function languageForCountry(code) {
  const c = String(code || '').toUpperCase()
  if (!c || MULTILINGUAL.has(c)) return ''
  return COUNTRY_LANGUAGE[c] || 'en'
}
